import React from "react";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import ProfileDocumentDropZone from "../shared/ProfileDocumentDropZone";
import { EMPTY_PROFILE_DOCUMENTS } from "./profileSnapshot";

export const DEFAULT_PROFILE_DOCUMENT_SECTIONS = {
  medical: true,
  medications: false,
  behavioral: false,
};
export const normalizeProfileDocuments = (documents = EMPTY_PROFILE_DOCUMENTS) => ({
  medical: Array.isArray(documents?.medical) ? documents.medical : [],
  medications: Array.isArray(documents?.medications) ? documents.medications : [],
  behavioral: Array.isArray(documents?.behavioral) ? documents.behavioral : [],
});

export const useProfileDocuments = ({
  storage,
  childId = "",
  initialDocuments = EMPTY_PROFILE_DOCUMENTS,
  setDocumentError,
} = {}) => {
  const documentInputRefs = React.useRef({});
  const documentSectionsRef = React.useRef(normalizeProfileDocuments(initialDocuments));
  const [uploadedDocuments, setUploadedDocumentsState] = React.useState(() => normalizeProfileDocuments(initialDocuments));
  const [openSections, setOpenSections] = React.useState(DEFAULT_PROFILE_DOCUMENT_SECTIONS);

  React.useEffect(() => {
    documentSectionsRef.current = uploadedDocuments;
  }, [uploadedDocuments]);

  const setUploadedDocuments = React.useCallback((nextDocuments) => {
    const normalizedDocuments = normalizeProfileDocuments(nextDocuments);
    documentSectionsRef.current = normalizedDocuments;
    setUploadedDocumentsState(normalizedDocuments);
  }, []);

  const resetDocuments = React.useCallback((nextDocuments = EMPTY_PROFILE_DOCUMENTS) => {
    setUploadedDocuments(nextDocuments);
    setOpenSections(DEFAULT_PROFILE_DOCUMENT_SECTIONS);
  }, [setUploadedDocuments]);

  const handleDocumentUpload = React.useCallback(async (category, event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";

    if (!files.length || !childId || !storage) {
      return;
    }

    setDocumentError?.("");

    try {
      const uploaded = await Promise.all(
        files.map(async (file) => {
          const fileRef = ref(
            storage,
            `children/${childId}/documents/${category}/${Date.now()}-${file.name}`
          );
          await uploadBytes(fileRef, file);
          const url = await getDownloadURL(fileRef);

          return {
            name: file.name,
            url,
            type: file.type || "application/octet-stream",
            category,
            uploadedAt: new Date().toISOString(),
          };
        })
      );

      const currentDocuments = documentSectionsRef.current;
      setUploadedDocuments({
        ...currentDocuments,
        [category]: [...(currentDocuments[category] || []), ...uploaded],
      });
    } catch (error) {
      console.error("Document upload failed:", error);
      setDocumentError?.("Document upload failed. Please try again.");
    }
  }, [childId, setDocumentError, setUploadedDocuments, storage]);

  const handleDocumentRemove = React.useCallback((category, targetIndex) => {
    const currentDocuments = documentSectionsRef.current;
    const currentCategoryDocuments = Array.isArray(currentDocuments?.[category])
      ? currentDocuments[category]
      : [];

    // TODO: Future document work should delete removed Storage files and add parsing/preview flows.
    setUploadedDocuments({
      ...currentDocuments,
      [category]: currentCategoryDocuments.filter((_item, index) => index !== targetIndex),
    });
  }, [setUploadedDocuments]);

  const renderDocumentDropZone = React.useCallback((category, title, helperText) => (
    <ProfileDocumentDropZone
      category={category}
      title={title}
      helperText={helperText}
      documents={uploadedDocuments?.[category] || []}
      inputRef={(node) => {
        documentInputRefs.current[category] = node;
      }}
      onUpload={handleDocumentUpload}
      onRemove={handleDocumentRemove}
    />
  ), [handleDocumentRemove, handleDocumentUpload, uploadedDocuments]);

  return {
    documentInputRefs,
    documentSectionsRef,
    uploadedDocuments,
    setUploadedDocuments,
    openSections,
    setOpenSections,
    resetDocuments,
    renderDocumentDropZone,
  };
};
